import { motion } from "framer-motion";
import { Instagram, MessageCircle, ArrowUpRight, Quote } from "lucide-react";

const bullets = [
    "9+ years in marketing across UAE",
    "50+ realtors positioned as market leaders",
    "AED 14 CPL &mdash; Dubai's lowest in the niche",
    "Personally on every shoot &amp; strategy call",
];

export default function Founder() {
    return (
        <section id="founder" className="relative py-24 sm:py-32 border-t border-white/10 overflow-hidden">
            <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-brand/10 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Left: portrait */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6 relative"
                    >
                        <div className="relative aspect-[529/918] overflow-hidden bg-[#0d0d0f] border border-white/10">
                            <img
                                src="/team/babur-portrait.jpg"
                                alt="Babur Ali — founder of Grow With Babur"
                                className="w-full h-full object-contain"
                                loading="lazy"
                            />
                            {/* Corner badge */}
                            <div className="absolute top-5 left-5 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                                <span className="font-mono text-[10px] uppercase tracking-widest text-white">Founder &amp; CEO</span>
                            </div>
                            {/* Bottom name plate */}
                            <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 bg-gradient-to-t from-black to-transparent">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-brand mb-2">Dubai &middot; UAE</div>
                                <div className="font-display font-semibold text-3xl sm:text-4xl text-white tracking-[-0.03em]">
                                    Babur Ali
                                </div>
                            </div>
                        </div>

                        {/* Floating stat card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.4 }}
                            className="hidden md:block absolute -bottom-6 -right-6 bg-brand p-5 shadow-2xl"
                        >
                            <div className="text-[10px] font-mono uppercase tracking-widest text-white/80">Since</div>
                            <div className="numeric-display font-semibold text-4xl text-white mt-1">2016</div>
                        </motion.div>
                    </motion.div>

                    {/* Right: story */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Meet the founder
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            The face behind<br />
                            <span className="text-brand">Grow With Babur.</span>
                        </h2>

                        <div className="mt-8 relative pl-6 border-l-2 border-brand/40">
                            <Quote className="absolute -left-3 top-0 w-6 h-6 text-brand bg-[#0A0A0A] p-1" />
                            <p className="text-white/85 text-lg leading-relaxed">
                                I spent 9+ years figuring out what makes a personal brand
                                actually convert &mdash; not just look pretty. Now I run that
                                exact playbook for Dubai's top realtors. If we work
                                together, you get me on every shoot, every strategy call,
                                every campaign review.
                            </p>
                        </div>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {bullets.map((b, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                                    className="flex items-start gap-3"
                                >
                                    <span className="font-mono text-brand text-xs pt-1">0{i + 1}</span>
                                    <span className="text-white/80 text-sm" dangerouslySetInnerHTML={{ __html: b }} />
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <a
                                href="https://instagram.com/growwithbabur"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="founder-instagram"
                                className="group inline-flex items-center justify-between gap-3 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-4 transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <Instagram className="w-5 h-5" /> @growwithbabur
                                </span>
                                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                            <a
                                href="https://wa.me/971543676278?text=Hi%20Babur!%20I%20want%20to%20talk."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-brand text-white font-semibold px-6 py-4 transition-colors"
                            >
                                <MessageCircle className="w-5 h-5" /> DM me directly
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
